import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Heart, Feather, ArrowLeft, Save, RefreshCw, 
  Calendar, ChevronRight, Loader2, Search, Star, X, WifiOff
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";

const JournalPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [prompts, setPrompts] = useState([]);
  const [todaysPrompt, setTodaysPrompt] = useState(null);
  const [entries, setEntries] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [journalContent, setJournalContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("prompts"); // prompts, write, history
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [pendingEntries, setPendingEntries] = useState([]);

  // Offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      syncPendingEntries();
    };
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Load pending entries from localStorage
    const cached = localStorage.getItem('pendingJournalEntries');
    if (cached) {
      setPendingEntries(JSON.parse(cached));
    }
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const fetchData = async () => {
    try {
      const [promptsRes, entriesRes] = await Promise.all([
        axios.get(`${API}/journal/prompts`),
        axios.get(`${API}/journal/entries`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setPrompts(promptsRes.data.prompts);
      setTodaysPrompt(promptsRes.data.todays_prompt);
      setEntries(entriesRes.data);
      
      // Cache entries for offline use
      localStorage.setItem('cachedJournalEntries', JSON.stringify(entriesRes.data));
    } catch (error) {
      console.error("Error fetching data:", error);
      // Try to load from cache if offline
      const cached = localStorage.getItem('cachedJournalEntries');
      if (cached) {
        setEntries(JSON.parse(cached));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) {
      fetchData();
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await axios.get(`${API}/journal/entries?search=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
    } catch (error) {
      console.error("Error searching:", error);
    } finally {
      setIsSearching(false);
    }
  }, [token]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (view === "history") {
        handleSearch(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, view, handleSearch]);

  const syncPendingEntries = async () => {
    if (pendingEntries.length === 0) return;
    
    for (const entry of pendingEntries) {
      try {
        await axios.post(`${API}/journal/entries`, entry, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error("Error syncing entry:", error);
        return; // Stop if error
      }
    }
    
    // Clear pending entries
    setPendingEntries([]);
    localStorage.removeItem('pendingJournalEntries');
    toast.success("Offline entries synced successfully!");
    fetchData();
  };

  const handleSelectPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setJournalContent("");
    setView("write");
  };

  const handleFreeDump = () => {
    setSelectedPrompt({ id: "free_write", prompt: "Today I...", category: "free_write" });
    setJournalContent("Today I ");
    setView("write");
  };

  const handleRandomPrompt = () => {
    const randomPrompt = prompts[Math.floor(Math.random() * prompts.length)];
    handleSelectPrompt(randomPrompt);
  };

  const handleSave = async () => {
    if (!journalContent.trim()) {
      toast.error("Please write something before saving.");
      return;
    }

    const entryData = {
      prompt_id: selectedPrompt?.id === "free_write" ? null : selectedPrompt?.id,
      prompt_text: selectedPrompt?.id === "free_write" ? "Today I... (free write)" : selectedPrompt?.prompt,
      content: journalContent
    };

    setSaving(true);
    
    // Handle offline mode
    if (isOffline) {
      const newPending = [...pendingEntries, { ...entryData, offline_created_at: new Date().toISOString() }];
      setPendingEntries(newPending);
      localStorage.setItem('pendingJournalEntries', JSON.stringify(newPending));
      toast.success("Saved offline. Will sync when you're back online.");
      setJournalContent("");
      setSelectedPrompt(null);
      setView("prompts");
      setSaving(false);
      return;
    }

    try {
      await axios.post(
        `${API}/journal/entries`,
        entryData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Your thoughts have been saved, beautiful soul.");
      setJournalContent("");
      setSelectedPrompt(null);
      setView("prompts");
      fetchData(); // Refresh entries
    } catch (error) {
      // Save offline if network error
      const newPending = [...pendingEntries, { ...entryData, offline_created_at: new Date().toISOString() }];
      setPendingEntries(newPending);
      localStorage.setItem('pendingJournalEntries', JSON.stringify(newPending));
      toast.warning("Saved offline. Will sync when connection is restored.");
      setJournalContent("");
      setSelectedPrompt(null);
      setView("prompts");
    } finally {
      setSaving(false);
    }
  };

  const categories = {
    self_discovery: "Self Discovery",
    forgiveness: "Forgiveness",
    self_love: "Self-Alignment",
    authenticity: "Authenticity",
    growth: "Growth",
    connection: "Connection"
  };

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <Feather className="w-8 h-8 text-primary" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient" data-testid="journal-page">
      {/* Offline Indicator */}
      {isOffline && (
        <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-2 text-center">
          <span className="text-sm text-amber-700 dark:text-amber-400 flex items-center justify-center gap-2">
            <WifiOff className="w-4 h-4" />
            You're offline. Entries will sync when you reconnect.
            {pendingEntries.length > 0 && ` (${pendingEntries.length} pending)`}
          </span>
        </div>
      )}

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => view === "prompts" ? navigate("/dashboard") : setView("prompts")}
            className="text-muted-foreground hover:text-foreground"
            data-testid="journal-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {view === "prompts" ? "Dashboard" : "Back"}
          </Button>
          <div className="flex items-center gap-2">
            <Feather className="w-5 h-5 text-primary" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Journal</span>
          </div>
          <Button
            variant="ghost"
            onClick={() => setView(view === "history" ? "prompts" : "history")}
            className="text-muted-foreground hover:text-foreground"
            data-testid="journal-history-btn"
          >
            <Calendar className="w-4 h-4 mr-2" />
            {view === "history" ? "Prompts" : "History"}
          </Button>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Prompts View */}
        {view === "prompts" && (
          <div className="animate-fade-in">
            <div className="text-center mb-10">
              <h1 className="font-heading text-3xl font-semibold text-foreground mb-3">
                Soulful Journalling
              </h1>
              <p className="text-muted-foreground text-lg max-w-xl mx-auto">
                Choose a prompt that speaks to you, or let the universe guide you
              </p>
            </div>

            {/* Today I... Free Write Box */}
            <Card 
              className="rounded-3xl border-0 mb-8 cursor-pointer transition-all hover:scale-[1.01] hover:shadow-lg overflow-hidden"
              style={{ background: "linear-gradient(135deg, #f0fdfa, #fefce8)" }}
              onClick={handleFreeDump}
              data-testid="free-write-card"
            >
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500/20 to-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Feather className="w-7 h-7 text-teal-600" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-wide font-medium text-teal-600 mb-1">Brain Dump</p>
                    <p className="font-heading text-xl md:text-2xl text-foreground">
                      Today I...
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      No prompt needed. Just write what's on your mind.
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                </div>
              </CardContent>
            </Card>

            {/* Today's Featured Prompt */}
            {todaysPrompt && (
              <Card 
                className="glass-card rounded-3xl border-2 border-primary/30 mb-8 cursor-pointer card-hover"
                onClick={() => handleSelectPrompt(todaysPrompt)}
                data-testid="todays-prompt"
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Star className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <span className="text-xs text-primary uppercase tracking-wide font-medium">Today's Prompt</span>
                      <p className="font-heading text-lg text-foreground mt-1 italic">
                        "{todaysPrompt.prompt}"
                      </p>
                      <span className="inline-block mt-2 text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                        {categories[todaysPrompt.category]}
                      </span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Random Prompt Button */}
            <div className="text-center mb-10">
              <Button
                onClick={handleRandomPrompt}
                className="btn-primary"
                data-testid="random-prompt-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Surprise Me with a Prompt
              </Button>
            </div>

            {/* Prompts by Category */}
            <div className="space-y-8">
              {Object.entries(categories).map(([key, label]) => {
                const categoryPrompts = prompts.filter(p => p.category === key);
                if (categoryPrompts.length === 0) return null;
                
                return (
                  <div key={key}>
                    <h2 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-primary"></span>
                      {label}
                    </h2>
                    <div className="grid gap-3">
                      {categoryPrompts.map((prompt) => (
                        <Card
                          key={prompt.id}
                          className="glass-card rounded-2xl border-0 cursor-pointer card-hover"
                          onClick={() => handleSelectPrompt(prompt)}
                          data-testid={`prompt-${prompt.id}`}
                        >
                          <CardContent className="p-5 flex items-center justify-between">
                            <p className="text-foreground leading-relaxed pr-4">
                              {prompt.prompt}
                            </p>
                            <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Write View */}
        {view === "write" && (
          <div className="animate-fade-in">
            <div className="text-center mb-8">
              <h1 className="font-heading text-2xl font-semibold text-foreground mb-2">
                Your Reflection Space
              </h1>
              <p className="text-muted-foreground">
                Let your thoughts flow freely, without judgement
              </p>
            </div>

            {/* Prompt Display */}
            {selectedPrompt && (
              <Card className="glass-card rounded-2xl border-0 mb-6">
                <CardContent className="p-6">
                  <span className="text-xs text-primary uppercase tracking-wide">
                    {selectedPrompt.id === "free_write" ? "Free Write" : "Today's Prompt"}
                  </span>
                  <p className="font-heading text-xl text-foreground mt-2 leading-relaxed" data-testid="selected-prompt">
                    {selectedPrompt.prompt}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Writing Area */}
            <Card className="glass-card rounded-3xl border-0 mb-6">
              <CardContent className="p-6">
                <Textarea
                  value={journalContent}
                  onChange={(e) => setJournalContent(e.target.value)}
                  placeholder="Begin writing here... let your heart speak..."
                  className="journal-textarea min-h-[300px] bg-transparent border-0 resize-none focus:ring-0 p-0 text-foreground placeholder:text-muted-foreground/50"
                  data-testid="journal-textarea"
                />
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                {journalContent.length} characters
              </p>
              <Button
                onClick={handleSave}
                disabled={saving || !journalContent.trim()}
                className="btn-primary"
                data-testid="save-journal-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Entry
              </Button>
            </div>
          </div>
        )}

        {/* History View */}
        {view === "history" && (
          <div className="animate-fade-in">
            <div className="text-center mb-8">
              <h1 className="font-heading text-3xl font-semibold text-foreground mb-3">
                Your Journey So Far
              </h1>
              <p className="text-muted-foreground text-lg">
                {entries.length} {entries.length === 1 ? "entry" : "entries"} written
              </p>
            </div>

            {/* Search Bar */}
            <div className="relative mb-8">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search your entries for patterns, keywords, insights..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-10 py-6 rounded-2xl bg-card border-border"
                data-testid="journal-search"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
              {isSearching && (
                <Loader2 className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 animate-spin text-primary" />
              )}
            </div>

            {searchQuery && entries.length > 0 && (
              <p className="text-sm text-muted-foreground mb-4">
                Found {entries.length} {entries.length === 1 ? "entry" : "entries"} matching "{searchQuery}"
              </p>
            )}

            {entries.length === 0 ? (
              <Card className="glass-card rounded-3xl border-0">
                <CardContent className="p-12 text-center">
                  <Feather className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Your journal awaits your first words, beautiful soul.
                  </p>
                  <Button
                    onClick={() => setView("prompts")}
                    className="btn-secondary mt-6"
                    data-testid="start-journaling-btn"
                  >
                    Start Journalling
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <ScrollArea className="h-[600px]">
                <div className="space-y-4 pr-4">
                  {entries.map((entry) => (
                    <Card key={entry.id} className="glass-card rounded-2xl border-0" data-testid={`entry-${entry.id}`}>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(entry.created_at), "d MMMM yyyy, h:mm a")}
                          </span>
                        </div>
                        {entry.prompt_text && (
                          <p className="text-sm text-primary italic mb-3">
                            "{entry.prompt_text}"
                          </p>
                        )}
                        <p className="text-foreground leading-relaxed whitespace-pre-wrap">
                          {entry.content}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default JournalPage;
